import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  AlertColor,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Operator, operatorTypeToDisplayText } from "./OperatorListPage";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { formatDate } from "../../../common/const";
import RequiredLabel from "../../atoms/RequiredLabel";
import {
  phoneNumberRegex,
  phoneNumberErrorMessage,
  emailRegex,
  emailErrorMessage,
} from "../../../utils/ValidationHelper";
import ConfirmDialog from "../../molecules/ConfirmDialog";

interface EditOperatorDialogProps {
  open: boolean;
  handleClose: () => void;
  handleEditOperatorSubmit: () => void;
  handleEditOperatorChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  editOperator: Operator;
  setEditOperator: React.Dispatch<React.SetStateAction<Operator>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
}

const EditOperatorModal: React.FC<EditOperatorDialogProps> = ({
  open,
  handleClose,
  handleEditOperatorSubmit,
  handleEditOperatorChange,
  editOperator,
  setEditOperator,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}) => {
  // 確認ダイアログ
  const [passwordUpdatedConfirmOpen, setPasswordUpdatedConfirmOpen] =
    React.useState(false);
  const [operatorDeleteConfirmOpen, setOperatorDeleteConfirmOpen] =
    React.useState(false);
  const [operatorDeletedConfirmOpen, setOperatorDeletedConfirmOpen] =
    React.useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [validationErrors, setValidationErrors] = useState({
    operatorName: "",
    departmentName: "",
    operatorPhoneNumber: "",
    operatorEmail: "",
    memo: "",
  });

  const handleValidation = () => {
    const errors = {
      operatorName: !editOperator.operatorName
        ? "氏名(部署名)は必須です"
        : editOperator.operatorName.length > CHARACTER_LIMIT
        ? `氏名(部署名)は${CHARACTER_LIMIT}文字以内で入力してください`
        : "",
      departmentName: editOperator.departmentName.length > CHARACTER_LIMIT
        ? `部署名(会社名)は${CHARACTER_LIMIT}文字以内で入力してください`
        : "",
      operatorPhoneNumber: !editOperator.operatorPhoneNumber || phoneNumberRegex.test(editOperator.operatorPhoneNumber)
        ? ""
        : phoneNumberErrorMessage,
      operatorEmail: !editOperator.operatorEmail || emailRegex.test(editOperator.operatorEmail)
        ? ""
        : emailErrorMessage,
      memo:
        editOperator.memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください`
          : "",
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleEditOperatorChange(event);
    // handleValidation();  // 入力のたびにバリデーションを実行し、入力が正しいとリアルタイムでエラーメッセージを削除　動きがおかしいので一旦コメントアウト
  };

  const handleUpdate = async () => {
    if (handleValidation()) {
      setIsLoading(true);
      try {
        await handleEditOperatorSubmit();
      } finally {
        setIsLoading(false);
      }
    }
  };

  const closeModal = () => {
    // エラーメッセージをリセット
    setValidationErrors({
      operatorName: "",
      departmentName: "",
      operatorPhoneNumber: "",
      operatorEmail: "",
      memo: "",
    });

    handleClose(); // 親コンポーネントの関数も実行
  };

  // パスワード更新API
  const handleRegeneratePassword = async () => {
    setIsLoading(true);
    try {
      setPasswordUpdatedConfirmOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // オペレーター削除API
  const handleDelete = async () => {
    setOperatorDeleteConfirmOpen(true);
  };

  return (
    <>
      <Dialog open={open} onClose={closeModal}>
        <DialogTitle>オペレーター編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="id"
            label="ID"
            type="text"
            name="id"
            value={editOperator.operatorId}
            fullWidth
            disabled
          />
          <TextField
            margin="dense"
            id="password"
            label="パスワード"
            type="text"
            name="password"
            value={editOperator.password}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={handleRegeneratePassword}>
                    <RefreshIcon />
                    <Typography variant="body2">パスワード更新</Typography>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled
          />
          <TextField
            margin="dense"
            id="operatorName"
            label={
              <span>
                氏名(部署名)
                <RequiredLabel />
              </span>
            }
            type="text"
            name="operatorName"
            value={editOperator.operatorName}
            fullWidth
            onChange={handleChange}
            error={!!validationErrors.operatorName}
            helperText={validationErrors.operatorName}
          />
          <TextField
            margin="dense"
            id="departmentName"
            label="部署名(会社名)"
            type="text"
            name="departmentName"
            value={editOperator.departmentName}
            fullWidth
            onChange={handleChange}
            error={!!validationErrors.departmentName}
            helperText={validationErrors.departmentName}
          />
          <TextField
            margin="dense"
            id="operatorPhoneNumber"
            label="連絡先(電話)"
            type="text"
            name="operatorPhoneNumber"
            value={editOperator.operatorPhoneNumber}
            fullWidth
            onChange={handleChange}
            error={!!validationErrors.operatorPhoneNumber}
            helperText={validationErrors.operatorPhoneNumber}
          />
          <TextField
            margin="dense"
            id="operatorEmail"
            label="連絡先(メール)"
            type="text"
            name="operatorEmail"
            value={editOperator.operatorEmail}
            fullWidth
            onChange={handleChange}
            error={!!validationErrors.operatorEmail}
            helperText={validationErrors.operatorEmail}
          />
          <TextField
            margin="dense"
            id="memo"
            label="メモ"
            type="text"
            name="memo"
            value={editOperator.memo}
            fullWidth
            onChange={handleChange}
            error={!!validationErrors.memo}
            helperText={validationErrors.memo}
          />
          <TextField
            margin="dense"
            id="operatorType"
            label="対象サービス"
            type="text"
            name="operatorType"
            value={operatorTypeToDisplayText(editOperator.operatorType)}
            fullWidth
            disabled
          />
          <TextField
            margin="dense"
            id="createdAt"
            label="登録日"
            type="text"
            name="createdAt"
            value={formatDate(editOperator.createdAt)}
            fullWidth
            disabled
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={isLoading}>
            キャンセル
          </Button>
          <Button onClick={handleDelete} color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "削除"}
          </Button>
          <Button onClick={handleUpdate} color="primary" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "更新"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={passwordUpdatedConfirmOpen}
        onClose={() => {
          setPasswordUpdatedConfirmOpen(false);
        }}
        onConfirm={async () => {
          setIsLoading(true);
          try {
            // JWTトークンを取得します
            const jwtToken = await getJwtTokenFromIdToken();
            // call updateOperatorPassword API
            const response = await fetch(
              process.env.REACT_APP_API_ENDPOINT +
                `/operators/${editOperator.autoOperatorId}/update-password`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: jwtToken,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              setEditOperator({ ...editOperator, password: data.password });
              setSnackbarMessage("パスワードが更新されました。");
              setAlertSeverity("success");
              setSnackbarOpen(true);
            } else {
              setSnackbarMessage("パスワードの更新に失敗しました。");
              setAlertSeverity("error");
              setSnackbarOpen(true);
            }
          } finally {
            setIsLoading(false);
          }
        }}
        title="確認"
        description="パスワードを更新しますか？"
      />
      <ConfirmDialog
        open={operatorDeleteConfirmOpen}
        onClose={() => {
          setOperatorDeleteConfirmOpen(false);
        }}
        onConfirm={async () => {
          setIsLoading(true);
          try {
            const jwtToken = await getJwtTokenFromIdToken();
            // call deleteOperator API
            const response = await fetch(
              process.env.REACT_APP_API_ENDPOINT +
                `/operators/${editOperator.autoOperatorId}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: jwtToken,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.result) {
                setOperatorDeletedConfirmOpen(true);
              } else {
                setSnackbarMessage("削除に失敗しました。");
                setAlertSeverity("error");
                setSnackbarOpen(true);
              }
            } else {
              setSnackbarMessage("削除に失敗しました。");
              setAlertSeverity("error");
              setSnackbarOpen(true);
            }
          } finally {
            setIsLoading(false);
          }
        }}
        title="確認"
        description="物件との紐付きも解除されますが、削除しますか？"
      />
      <ConfirmDialog
        open={operatorDeletedConfirmOpen}
        onClose={() => {
          setOperatorDeletedConfirmOpen(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description="削除が完了しました。"
        showCancelButton={false}
      />
    </>
  );
};

export default EditOperatorModal;
